import { useEffect, useState } from "react";
import { LocalizedLink } from "@/components/LocalizedLink";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useExperienceReviews } from "@/hooks/use-experience-reviews";
import { useUiTranslations } from "@/hooks/use-ui-translations";
import { useCurrency } from "@/contexts/CurrencyContext";
import { calculateGrossPrice, resolveMarkup } from "@/lib/pricing";
import { IconPetal } from "@/components/icons";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useRef } from "react";

const STORAGE_KEY = "guestly_recently_viewed";
const MAX_ITEMS = 10;

/** Record a viewed experience slug in localStorage */
export function trackRecentlyViewed(slug: string) {
  try {
    const existing: string[] = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    const filtered = existing.filter((s) => s !== slug);
    filtered.unshift(slug);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered.slice(0, MAX_ITEMS)));
  } catch {
    // silent
  }
}

export function getRecentSlugs(): string[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
  } catch {
    return [];
  }
}

interface RecentExperience {
  id: string;
  title: string;
  slug: string;
  images: string[] | null;
  duration_minutes: number;
  location: string | null;
  pricing_model: string;
  net_price_per_person: number | null;
  net_price_per_group: number | null;
  min_group_size: number | null;
  max_group_size: number | null;
  provider_id: string;
}

export function RecentlyViewedCarousel() {
  const [slugs, setSlugs] = useState<string[]>([]);
  const { t } = useUiTranslations();
  const { formatPrice } = useCurrency();
  const { data: reviewStats } = useExperienceReviews();
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSlugs(getRecentSlugs());
  }, []);

  const { data: experiences } = useQuery({
    queryKey: ["recently-viewed", slugs],
    queryFn: async () => {
      if (!slugs.length) return [];
      const { data, error } = await supabase
        .from("experiences")
        .select("id, title, slug, images, duration_minutes, location, pricing_model, net_price_per_person, net_price_per_group, min_group_size, max_group_size, provider_id")
        .in("slug", slugs)
        .eq("status", "active");
      if (error) throw error;

      // Fetch markup rules for pricing
      const providerIds = [...new Set((data ?? []).map((e) => e.provider_id))];
      const { data: markupRules } = await supabase
        .from("markup_rules")
        .select("provider_id, experience_id, markup_percent");

      const rules = markupRules ?? [];

      // Sort by the order they appear in slugs
      const sorted = slugs
        .map((s) => (data ?? []).find((e) => e.slug === s))
        .filter(Boolean) as RecentExperience[];

      return sorted.map((exp) => {
        const markup = resolveMarkup(
          rules.map((r) => ({
            provider_id: r.provider_id,
            experience_id: r.experience_id,
            markup_percent: r.markup_percent,
          })),
          exp.provider_id,
          exp.id
        );
        const grossPP = exp.net_price_per_person
          ? calculateGrossPrice(exp.net_price_per_person, markup)
          : null;
        const grossPG = exp.net_price_per_group
          ? calculateGrossPrice(exp.net_price_per_group, markup)
          : null;
        return { ...exp, grossPricePerPerson: grossPP, grossPricePerGroup: grossPG };
      });
    },
    enabled: slugs.length > 0,
    staleTime: 60_000,
  });

  if (!experiences?.length) return null;

  function formatDuration(minutes: number): string {
    if (minutes < 60) return `${minutes} min`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}h ${m}m` : `${h === 1 ? "1 timme" : `${h} timmar`}`;
  }

  const scroll = (dir: "left" | "right") => {
    if (!scrollRef.current) return;
    const amount = 300;
    scrollRef.current.scrollBy({ left: dir === "left" ? -amount : amount, behavior: "smooth" });
  };

  return (
    <div className="relative w-full max-w-5xl mx-auto">
      <h3 className="text-center text-sm font-semibold text-white/90 tracking-wide mb-3 italic">
        {t("hero.continuePlanning") || "Fortsätt planera din resa"}
      </h3>

      <div className="relative group">
        {/* Scroll buttons */}
        <button
          onClick={() => scroll("left")}
          className="absolute -left-3 top-1/2 -translate-y-1/2 z-10 h-9 w-9 rounded-full bg-card/90 border border-border shadow-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          aria-label="Scroll left"
        >
          <ChevronLeft className="h-4 w-4 text-foreground" />
        </button>
        <button
          onClick={() => scroll("right")}
          className="absolute -right-3 top-1/2 -translate-y-1/2 z-10 h-9 w-9 rounded-full bg-card/90 border border-border shadow-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          aria-label="Scroll right"
        >
          <ChevronRight className="h-4 w-4 text-foreground" />
        </button>

        <div
          ref={scrollRef}
          className="flex gap-3 overflow-x-auto scrollbar-hide snap-x snap-mandatory px-1 pb-1"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {experiences.map((exp) => {
            const review = reviewStats?.[exp.id];
            const price = exp.grossPricePerPerson
              ? formatPrice(exp.grossPricePerPerson)
              : exp.grossPricePerGroup
                ? formatPrice(exp.grossPricePerGroup)
                : null;

            return (
              <LocalizedLink
                key={exp.id}
                to={`/experiences/${(exp as any).city_slug ?? "unknown"}/${exp.slug}`}
                className="flex-shrink-0 snap-start w-[260px] sm:w-[290px] group/card rounded-xl bg-card/95 backdrop-blur-sm border border-white/10 shadow-lg overflow-hidden hover:shadow-xl transition-all hover:scale-[1.02]"
              >
                <div className="flex items-stretch h-[100px]">
                  {/* Image */}
                  {exp.images?.[0] && (
                    <div className="w-[100px] h-full flex-shrink-0 overflow-hidden">
                      <img
                        src={exp.images[0]}
                        alt={exp.title}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  )}
                  {/* Info */}
                  <div className="flex-1 p-2.5 flex flex-col justify-between min-w-0">
                    <div>
                      <p className="text-xs font-semibold text-foreground line-clamp-2 leading-tight">
                        {exp.title}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-1">
                        {formatDuration(exp.duration_minutes)}
                        {exp.min_group_size && exp.max_group_size
                          ? ` · ${exp.min_group_size}–${exp.max_group_size} pers`
                          : ""}
                      </p>
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      {review && review.count > 0 ? (
                        <div className="flex items-center gap-0.5">
                          <IconPetal className="h-3 w-3 fill-amber-400 text-amber-400" filled />
                          <span className="text-[10px] font-semibold text-foreground">{review.avgScore}</span>
                          <span className="text-[10px] text-muted-foreground">({review.count})</span>
                        </div>
                      ) : (
                        <span />
                      )}
                      {price && (
                        <span className="text-xs font-bold text-foreground">
                          {t("card.from") || "Från"} {price}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </LocalizedLink>
            );
          })}
        </div>
      </div>
    </div>
  );
}
